"use client";
import { createContext, Dispatch, useContext, useReducer } from "react";

type FilterValue = string | string[];

export type Filters = Record<string, FilterValue>;

export type ProductFilteringContextType = {
  isOpen: boolean;
  filters: Filters;
};

const initialFilteringState: ProductFilteringContextType = {
  isOpen: false,
  filters: {},
};

export const FILTERING_ACTIONS = {
  UPDATE_FILTER: "updateFilter",
  ADD_FILTER: "addFilter",
  REMOVE_FILTER: "removeFilter",
  CHANGE_FILTER: "changeFilter",
  CLEAR_FILTER: "clearFilter",
  CLEAR_ALL_FILTERS: "clearAllFilters",
  SET_IS_OPEN: "setIsOpen",
} as const;

type UpdateFilterAction = {
  type: typeof FILTERING_ACTIONS.UPDATE_FILTER;
  filter: string;
  values: string[];
};

type AddFilterAction = {
  type: typeof FILTERING_ACTIONS.ADD_FILTER;
  filter: string;
  value: string;
};

type RemoveFilterAction = {
  type: typeof FILTERING_ACTIONS.REMOVE_FILTER;
  filter: string;
  value: string;
};

type ChangeFilterAction = {
  type: typeof FILTERING_ACTIONS.CHANGE_FILTER;
  filter: string;
  value: string;
};

type ClearFilterAction = {
  type: typeof FILTERING_ACTIONS.CLEAR_FILTER;
  filter: string;
};

type ClearAllFiltersAction = {
  type: typeof FILTERING_ACTIONS.CLEAR_ALL_FILTERS;
};

type SetIsOpenAction = {
  type: typeof FILTERING_ACTIONS.SET_IS_OPEN;
  isOpen: boolean;
};

type Action =
  | UpdateFilterAction
  | AddFilterAction
  | RemoveFilterAction
  | ChangeFilterAction
  | ClearFilterAction
  | ClearAllFiltersAction
  | SetIsOpenAction;

const reducer = (
  state: ProductFilteringContextType,
  action: Action,
): ProductFilteringContextType => {
  switch (action.type) {
    case FILTERING_ACTIONS.UPDATE_FILTER: {
      const { filter, values } = action;
      return {
        ...state,
        filters: {
          ...state.filters,
          [filter]: values,
        },
      };
    }
    case FILTERING_ACTIONS.ADD_FILTER: {
      const { filter, value } = action;
      const currentValue = state.filters[filter];

      if (currentValue === undefined) {
        return {
          ...state,
          filters: {
            ...state.filters,
            [filter]: [value],
          },
        };
      }

      return {
        ...state,
        filters: {
          ...state.filters,
          [filter]: [...currentValue, value],
        },
      };
    }
    case FILTERING_ACTIONS.REMOVE_FILTER: {
      const { filter, value } = action;
      const currentValue = state.filters[filter];

      if (!currentValue) {
        return state;
      }

      return {
        ...state,
        filters: {
          ...state.filters,
          [filter]: (currentValue as string[]).filter((v) => v !== value),
        },
      };
    }
    case FILTERING_ACTIONS.CHANGE_FILTER: {
      const { filter, value } = action;
      return {
        ...state,
        filters: {
          ...state.filters,
          [filter]: value,
        },
      };
    }
    case FILTERING_ACTIONS.CLEAR_FILTER: {
      const { filter } = action;
      return {
        ...state,
        filters: {
          ...state.filters,
          [filter]: [],
        },
      };
    }
    case FILTERING_ACTIONS.SET_IS_OPEN:
      return { ...state, isOpen: action.isOpen };
    case FILTERING_ACTIONS.CLEAR_ALL_FILTERS: {
      const clearedFilters = Object.keys(state.filters).reduce(
        (acc, key) => ({
          ...acc,
          [key]: Array.isArray(state.filters[key]) ? [] : "",
        }),
        {},
      );

      return { ...state, filters: clearedFilters };
    }
    default:
      return state;
  }
};

type ProductFilteringContextValue = {
  state: ProductFilteringContextType;
  dispatch: Dispatch<Action>;
};

export const ProductFilteringContext =
  createContext<ProductFilteringContextValue>({
    state: initialFilteringState,
    dispatch: () => undefined,
  });

export const ProductFilteringProvider = ({
  initialFilters,
  children,
}: {
  initialFilters: Filters;
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, {
    isOpen: false,
    filters: initialFilters,
  });

  return (
    <ProductFilteringContext.Provider value={{ state, dispatch }}>
      {children}
    </ProductFilteringContext.Provider>
  );
};

export const useProductFiltering = () => useContext(ProductFilteringContext);
